import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ZoomIn, ZoomOut, ArrowLeft, ArrowRight, RotateCw, Lock, Mail, ChevronDown } from 'lucide-react';
import LogoUploader, { FileFormData } from '../components/LogoUploader';
import { useAuthContext } from '../../../features/auth/data/context/AuthContext';
import Alert from '../../../shared/components/Alert';
import Loader from '../../../shared/components/Loader';
import '../styles/WhiteLabelPage.css';

const WhiteLabelPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, uploadAdminLogo } = useAuthContext();

  const [logoFile, setLogoFile] = useState<FileFormData | null>(null);
  const [faviconFile, setFaviconFile] = useState<FileFormData | null>(null);
  const [isLogoReset, setIsLogoReset] = useState(false);
  const [isFaviconReset, setIsFaviconReset] = useState(false);
  const [logoZoom, setLogoZoom] = useState(100);
  const [faviconZoom, setFaviconZoom] = useState(100);
  const [logoResetKey, setLogoResetKey] = useState(0);
  const [faviconResetKey, setFaviconResetKey] = useState(0);

  const [isSaving, setIsSaving] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'warning' | 'info',
  });

  useEffect(() => {
    // Redirect if not Super Admin
    if (state.user && state.user.role_name !== 'Super Admin') {
      navigate('/dashboard');
    }
  }, [state.user, navigate]);

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setAlertConfig({ show: true, title, message, type });
  };

  const handleReset = (type: 'logo' | 'favicon') => {
    if (type === 'logo') {
      setLogoFile(null);
      setIsLogoReset(true);
      setLogoZoom(100);
      setLogoResetKey(k => k + 1);
    } else {
      setFaviconFile(null);
      setIsFaviconReset(true);
      setFaviconZoom(100);
      setFaviconResetKey(k => k + 1);
    }
  };

  const handleSave = async () => {
    if (!logoFile && !faviconFile && !isLogoReset && !isFaviconReset) {
      showAlert('Warning', 'No changes to save.', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      if (logoFile) {
        const blob = new Blob([logoFile.binary], { type: logoFile.type });
        formData.append('logo', blob, logoFile.name);
      }
      if (faviconFile) {
        const blob = new Blob([faviconFile.binary], { type: faviconFile.type });
        formData.append('favicon', blob, faviconFile.name);
      }

      const queryParams = [];
      if (isLogoReset && !logoFile) queryParams.push('reset_logo=true');
      if (isFaviconReset && !faviconFile) queryParams.push('reset_favicon=true');
      const queryStr = queryParams.length > 0 ? queryParams.join('&') : undefined;

      const res = await uploadAdminLogo(formData, queryStr);
      if (res.success) {
        showAlert('Success', 'Successfully updated branding.', 'success');
        setLogoFile(null);
        setFaviconFile(null);
        setIsLogoReset(false);
        setIsFaviconReset(false);
        setLogoZoom(100);
        setFaviconZoom(100);
      } else {
        showAlert('Error', res.error || 'Failed to update branding.', 'error');
      }
    } catch (error) {
      showAlert('Error', 'An unexpected error occurred while saving.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setLogoFile(null);
    setFaviconFile(null);
    setIsLogoReset(false);
    setIsFaviconReset(false);
    setLogoZoom(100);
    setFaviconZoom(100);
    navigate('/dashboard');
  };

  const previewLogoSrc = logoFile?.preview || (!isLogoReset && state.user?.logo_url) || '/logo_name.png';
  const previewFaviconSrc = faviconFile?.preview || (!isFaviconReset && state.user?.favicon_url) || '/logo.png';

  const hasChanges = logoFile !== null || faviconFile !== null || isLogoReset || isFaviconReset;

  // Derive dynamic user details for the mock browser
  const profile = state.user;
  const userName = (profile as any)?.admin_username || (profile as any)?.full_name || profile?.company_name || 'Institute';
  const initials = userName
    ? userName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'A';

  return (
    <div className="wl-page">
      <Loader isLoading={isSaving} message="Saving branding settings..." />
      <Alert
        show={alertConfig.show}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        onClose={() => setAlertConfig({ ...alertConfig, show: false })}
        primaryButtonText="OK"
      />

      <div className="wl-main">
        <div className="wl-header">
          <div className="wl-header-info">
            <div className="wl-title-row">
              <h1>White Label Settings</h1>
              <span className="wl-header-desc">Customize the logo and favicon for your Interview Admin application.</span>
            </div>
          </div>
        </div>

        <div className="wl-content-grid">
          {/* Upload Section */}
          <div className="wl-upload-section">
            {/* Logo Upload Card */}
            <div className="wl-card">
              <div className="wl-card-header">
                <h3>Logo</h3>
                {(!!state.user?.logo_url || !!logoFile) && !isLogoReset && (
                  <button className="wl-reset-btn" onClick={() => handleReset('logo')}>
                    Reset to Default
                  </button>
                )}
              </div>
              <LogoUploader
                type="logo"
                zoom={logoZoom}
                defaultImage={(!isLogoReset && state.user?.logo_url) || '/logo_name.png'}
                onFileSelect={(file) => { setLogoFile(file); setIsLogoReset(false); }}
                onAlert={(msg) => showAlert('Upload Error', msg, 'error')}
                resetKey={logoResetKey}
              />
              <div className="wl-slider-container">
                <ZoomOut size={16} className="wl-slider-icon" />
                <input
                  type="range"
                  min="50"
                  max="150"
                  value={logoZoom}
                  onChange={(e) => setLogoZoom(Number(e.target.value))}
                  className="wl-slider"
                />
                <ZoomIn size={16} className="wl-slider-icon" />
              </div>
            </div>

            {/* Favicon Upload Card */}
            <div className="wl-card">
              <div className="wl-card-header">
                <h3>Favicon</h3>
                {(!!state.user?.favicon_url || !!faviconFile) && !isFaviconReset && (
                  <button className="wl-reset-btn" onClick={() => handleReset('favicon')}>
                    Reset to Default
                  </button>
                )}
              </div>
              <LogoUploader
                type="favicon"
                zoom={faviconZoom}
                defaultImage={(!isFaviconReset && state.user?.favicon_url) || '/logo_name.png'}
                onFileSelect={(file) => { setFaviconFile(file); setIsFaviconReset(false); }}
                onAlert={(msg) => showAlert('Upload Error', msg, 'error')}
                resetKey={faviconResetKey}
              />
              <div className="wl-slider-container">
                <ZoomOut size={16} className="wl-slider-icon" />
                <input
                  type="range"
                  min="50"
                  max="150"
                  value={faviconZoom}
                  onChange={(e) => setFaviconZoom(Number(e.target.value))}
                  className="wl-slider"
                />
                <ZoomIn size={16} className="wl-slider-icon" />
              </div>
            </div>
          </div>

          {/* Live Preview Section */}
          <div className="wl-preview-section">
            <div className="wl-browser-mockup">
              {/* Browser Header Top (Tabs) */}
              <div className="wl-browser-top-bar">
                <div className="wl-browser-dots">
                  <div className="wl-browser-dot wl-dot-red"></div>
                  <div className="wl-browser-dot wl-dot-yellow"></div>
                  <div className="wl-browser-dot wl-dot-green"></div>
                </div>
                <div className="wl-browser-tab">
                  <img src={previewFaviconSrc} alt="favicon" className="wl-browser-favicon" />
                  <span>Joz HRM Corporate</span>
                </div>
              </div>

              {/* Browser Toolbar (Address Bar) */}
              <div className="wl-browser-toolbar">
                <div className="wl-browser-nav-icons">
                  <ArrowLeft size={16} className="wl-nav-icon" />
                  <ArrowRight size={16} className="wl-nav-icon wl-nav-icon-disabled" />
                  <RotateCw size={16} className="wl-nav-icon" />
                </div>
                <div className="wl-browser-address-bar">
                  <Lock size={12} className="wl-lock-icon" />
                  <span>interviewcorp.jozuna.com/</span>
                </div>
              </div>

              {/* Browser Body */}
              <div className="wl-browser-body">

                {/* Mock Topbar */}
                <div className="wl-mock-topbar">
                  <div className="wl-mock-topbar-left">
                  </div>

                  <div className="wl-mock-topbar-center" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
                      <img src={previewLogoSrc} alt="Company Logo" style={{ height: '25px', objectFit: 'contain' }} />
                      {previewLogoSrc !== '/logo_name.png' && (
                        <>
                          <div style={{ height: '16px', width: '1px', backgroundColor: '#cbd5e1' }} />
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: 'linear-gradient(145deg, #f0f9ff, #f5f3ff)',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                            boxShadow: '0 4px 15px rgba(14, 165, 233, 0.15)',
                            padding: '2px 10px', borderRadius: '24px',
                            color: '#475569',
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '7px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '700', paddingTop: '2px' }}>powered by</span>
                              <img src="/logo_name_only.png" alt="JOZhrm" style={{ height: '10px', objectFit: 'contain' }} />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="wl-mock-topbar-right">
                    <div className="wl-mock-topbar-actions">
                      <div className="wl-mock-action-btn">
                        <Mail size={16} />
                      </div>
                      <div className="wl-mock-profile-trigger">
                        <div className="wl-mock-user-avatar">
                          <span>{initials}</span>
                        </div>
                        <span className="wl-mock-user-name">{userName}</span>
                        <ChevronDown size={12} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="wl-mock-bottom-container">
                  {/* Mock Sidebar */}
                  <div className="wl-mock-sidebar">

                    <div className="wl-mock-sidebar-nav">
                      <div className="wl-mock-nav-item active"></div>
                      <div className="wl-mock-nav-item"></div>
                      <div className="wl-mock-nav-item"></div>
                      <div className="wl-mock-nav-item"></div>
                    </div>
                  </div>
                  {/* Mock Content */}
                  <div className="wl-mock-content">
                    <div className="wl-mock-card"></div>
                    <div className="wl-mock-card" style={{ width: '60%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="wl-footer">
        <button className="wl-btn-secondary" onClick={handleCancel}>
          Cancel
        </button>
        <button
          className="wl-btn-primary"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default WhiteLabelPage;
